import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

const RfqResponsesSummary = () => {
    const { rfqId } = useParams();
    const [rfq, setRfq] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAwarding, setIsAwarding] = useState(false);

    const fetchRfqDetails = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get(`/rfqs/${rfqId}/`);
            setRfq(response.data);
        } catch (err) {
            console.error(`Error fetching RFQ ${rfqId} details:`, err);
            setError(`Failed to load RFQ details. ${err.response?.data?.detail || err.response?.data?.error || err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [rfqId]);

    useEffect(() => {
        fetchRfqDetails();
    }, [fetchRfqDetails]);

    const handleAwardRfq = async (invitationId, vendorName) => {
        const notes = prompt(`Enter any award notes for RFQ #${rfq?.id || rfqId} awarded to ${vendorName} (optional):`);
        
        if (notes === null) {
            return;
        }

        if (!window.confirm(`Are you sure you want to award RFQ #${rfq?.id || rfqId} to ${vendorName}? ${notes ? `With notes: "${notes}"` : ''} This action will notify vendors and cannot be easily undone.`)) {
            return;
        }

        setIsAwarding(true);
        try {
            const payload = {
                invitation_id: invitationId,
                award_notes: notes || ''
            };
            const response = await api.post(`/rfqs/${rfq?.id || rfqId}/award/`, payload);
            alert(`RFQ #${rfq?.id || rfqId} successfully awarded to ${vendorName}!`);
            setRfq(response.data);
        } catch (err) {
            console.error("Error awarding RFQ:", err);
            const errorMessage = err.response?.data?.error ||
                                 err.response?.data?.detail ||
                                 (typeof err.response?.data === 'object' ? JSON.stringify(err.response.data) : null) ||
                                 err.message ||
                                 'An unexpected error occurred while awarding the RFQ.';
            alert(`Failed to award RFQ: ${errorMessage}`);
        } finally {
            setIsAwarding(false);
        }
    };

    if (isLoading) {
        return <div className="text-center p-10">Loading RFQ Responses Summary...</div>;
    }

    if (error && !rfq) {
        return (
            <div className="text-center p-10 text-red-600 font-bold">
                <p>{error}</p>
                <Link to="/rfqs" className="mt-4 inline-block text-indigo-600 hover:underline">
                    Back to RFQ List
                </Link>
            </div>
        );
    }
    
    if (!rfq) {
        return (
            <div className="text-center p-10">
                <p>RFQ not found or failed to load.</p>
                <Link to="/rfqs" className="mt-4 inline-block text-indigo-600 hover:underline">
                    Back to RFQ List
                </Link>
            </div>
        );
    }

    const submittedInvitations = rfq.invitations?.filter(inv => inv.status === 'SUBMITTED') || [];
    
    const formatCurrency = (value) => {
        const num = parseFloat(value);
        return !isNaN(num) ? `$${num.toFixed(2)}` : 'N/A';
    };

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
            <div className="max-w-full mx-auto">
                <header className="mb-8">
                     <Link to={`/rfqs/${rfqId}`} className="text-indigo-600 hover:underline mb-4 inline-block">
                        ← Back to RFQ Detail
                    </Link>
                    <h1 className="text-3xl font-extrabold text-gray-900">
                        Responses Summary for RFQ #{rfq.id} - {rfq.name}
                    </h1>
                    <p className="mt-1 text-lg text-gray-600">{rfq.short_description}</p>
                    {rfq.status && (
                        <p className="mt-2 text-sm">
                            Status: <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                rfq.status === 'AWARDED' ? 'bg-green-100 text-green-700' :
                                rfq.status === 'OPEN' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                            }`}>
                                {rfq.status}
                            </span>
                        </p>
                    )}
                     {rfq.status === 'AWARDED' && rfq.awarded_invitation?.vendor && (
                        <p className="mt-1 text-sm text-green-600 font-medium">
                            Awarded to: {rfq.awarded_invitation.vendor.name}
                        </p>
                    )}
                </header>

                {submittedInvitations.length === 0 ? (
                    <div className="text-center py-10 bg-white rounded-lg shadow">
                        <p className="text-gray-500 italic">No submitted responses found for this RFQ yet.</p>
                    </div>
                ) : (
                    <div className="bg-white shadow-xl rounded-lg overflow-x-auto border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-100 z-10">Vendor</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Overall Cost</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Other Costs</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Timeline</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Considerations</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Alternative Options</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Submitted At</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {submittedInvitations.map((invitation) => {
                                    const isThisInvitationAwarded = rfq.status === 'AWARDED' && rfq.awarded_invitation?.id === invitation.id;
                                    const isRfqAwardedToSomeoneElse = rfq.status === 'AWARDED' && rfq.awarded_invitation?.id !== invitation.id;

                                    return (
                                        <tr key={invitation.id} className={`transition-colors duration-150 group ${isThisInvitationAwarded ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-blue-50'}`}>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 group-hover:bg-opacity-80 z-0 ${isThisInvitationAwarded ? 'bg-green-50 group-hover:bg-green-100' : 'bg-white group-hover:bg-blue-50'}`}>
                                                {invitation.vendor?.name || 'N/A'}
                                                {isThisInvitationAwarded && ( <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-white bg-green-500 rounded-full">Awarded</span> )}
                                            </td>
                                            
                                            {/* --- ROBUST FIX APPLIED HERE: Check top-level field, then fall back to response_data --- */}
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold">
                                                {formatCurrency(invitation.overall_cost ?? invitation.response_data?.overall_cost)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {formatCurrency(invitation.other_costs ?? invitation.response_data?.other_costs)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-normal text-sm text-gray-700 min-w-[200px]">
                                                {(invitation.vendor_timeline || invitation.response_data?.vendor_timeline) || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-normal text-sm text-gray-700 min-w-[250px]">
                                                {(invitation.considerations || invitation.response_data?.considerations) || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-normal text-sm text-gray-700 min-w-[250px]">
                                                {(invitation.alternative_options || invitation.response_data?.alternative_options) || 'N/A'}
                                            </td>
                                            
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{invitation.submitted_at ? new Date(invitation.submitted_at).toLocaleString() : 'N/A'}</td>
                                            
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                {rfq.status !== 'AWARDED' && (
                                                    <button
                                                        onClick={() => handleAwardRfq(invitation.id, invitation.vendor?.name)}
                                                        className="text-white bg-green-600 hover:bg-green-700 px-3 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                                        disabled={isAwarding}
                                                    >
                                                        {isAwarding ? 'Processing...' : 'Award'}
                                                    </button>
                                                )}
                                                {isThisInvitationAwarded && ( <span className="text-green-600 font-semibold">Awarded Here</span> )}
                                                {isRfqAwardedToSomeoneElse && ( <span className="text-gray-500 text-xs">RFQ Awarded Elsewhere</span> )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RfqResponsesSummary;